import type { KeyboardEvent, MouseEvent } from "react"

import { useTranslation } from "react-i18next"

import type { WorkflowItem } from "@/api/models"

import { CheckCircle2, Circle, Loader2, Play, User, Users, Workflow } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"

type WorkflowSidebarItemProps = {
  currentWorkflowId?: string
  selectMode?: boolean
  isSelected?: boolean
  isMobile: boolean
  runningWorkflowId: string | null
  showDeleteAction: boolean
  workflow: WorkflowItem
  onClick: () => void
  onSelect?: (
    event: MouseEvent<HTMLDivElement> | KeyboardEvent<HTMLDivElement>,
    workflow: WorkflowItem
  ) => void
  onSelectModeToggle?: (workflow: WorkflowItem) => void
  onRun: () => void
}

export function WorkflowSidebarItem({
  currentWorkflowId,
  selectMode = false,
  isSelected = false,
  isMobile,
  runningWorkflowId,
  showDeleteAction,
  workflow,
  onClick,
  onSelect,
  onSelectModeToggle,
  onRun,
}: WorkflowSidebarItemProps) {
  const { t } = useTranslation()
  const isCurrentWorkflow = currentWorkflowId === workflow.id
  const shouldShowSelectAction = showDeleteAction && !selectMode && onSelectModeToggle

  const handleItemClick = (event: MouseEvent<HTMLDivElement>) => {
    if (selectMode && onSelect) {
      onSelect(event, workflow)
      return
    }
    onClick()
  }

  const getWorkflowIcon = () => {
    return workflow.visibility === "company" ? (
      <Users className="size-4" />
    ) : (
      <User className="size-4" />
    )
  }

  return (
    <Tooltip>
      <div
        role="presentation"
        className={cn(
          "group/item relative isolate flex w-full cursor-pointer items-center rounded-md",
          "hover:text-sidebar-accent-foreground",
          isCurrentWorkflow && "text-sidebar-accent-foreground"
        )}
        // Mouse convenience for the whole row; keyboard users activate the inner
        // TooltipTrigger button, whose click bubbles up to this handler.
        onClick={handleItemClick}
      >
        <div
          aria-hidden="true"
          className={cn(
            "bg-sidebar-accent pointer-events-none absolute inset-0 -z-10 rounded-md transition-opacity duration-150",
            isCurrentWorkflow ? "opacity-100" : "opacity-0 group-hover/item:opacity-100"
          )}
        />
        <TooltipTrigger
          className={cn(
            "flex w-full items-center justify-start gap-2 py-1.5 pl-2",
            showDeleteAction ? "pr-20" : "pr-14"
          )}
        >
          {runningWorkflowId === workflow.id ? (
            <Loader2 className="text-muted-foreground size-3.5 shrink-0 animate-spin" />
          ) : (
            <Workflow className="text-muted-foreground size-3.5 shrink-0" />
          )}
          <span className="truncate text-left text-sm">{workflow.title}</span>
        </TooltipTrigger>
        <div className="absolute top-1/2 right-1 flex -translate-y-1/2 items-center gap-0.5">
          <Button
            aria-label={t("questioninput.workflow_selection.run")}
            disabled={runningWorkflowId === workflow.id}
            size="sm"
            variant="ghost"
            className={cn(
              "workflow-sidebar-touch-action h-6 gap-1 rounded-full px-2.5 text-xs font-medium transition-opacity duration-150",
              "hover:bg-foreground/15",
              isMobile || runningWorkflowId === workflow.id
                ? "opacity-100"
                : "opacity-0 group-hover/item:opacity-100"
            )}
            onClick={(e) => {
              e.stopPropagation()
              onRun()
            }}
          >
            {runningWorkflowId === workflow.id ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <Play className="size-3 fill-current" />
            )}
            {t("questioninput.workflow_selection.run", { defaultValue: "Run" })}
          </Button>
          {shouldShowSelectAction && (
            <Button
              aria-label={t("workflows.select", { defaultValue: "Select" })}
              size="sm"
              variant="ghost"
              className={cn(
                "workflow-sidebar-touch-action hover:bg-foreground/15 size-6 p-0 transition-opacity duration-150",
                isMobile ? "opacity-100" : "opacity-0 group-hover/item:opacity-100"
              )}
              onClick={(e) => {
                e.stopPropagation()
                onSelectModeToggle(workflow)
              }}
            >
              <Circle className="size-4" />
            </Button>
          )}
          {selectMode && showDeleteAction && (
            <div className="ml-0.5 flex items-center justify-center">
              {isSelected ? (
                <CheckCircle2 className="text-primary size-5" />
              ) : (
                <Circle className="text-muted-foreground size-5" />
              )}
            </div>
          )}
        </div>
      </div>
      <TooltipContent className="max-w-[280px] p-3" side="right" sideOffset={8}>
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            {getWorkflowIcon()}
            <p className="text-sm font-medium">{workflow.title}</p>
          </div>
          {workflow.category && (
            <p className="text-muted-foreground text-xs">{workflow.category}</p>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  )
}
